import { AppSettings } from "@/AppSettings";
import { ConfigurationStatus, CoordinatingGroup, Institution, MasterGroup, Trial } from "@/api/dtos";
import CoordinatingGroupsContext from "@/contexts/data/CoordinatingGroupsContext";
import MasterGroupsContext from "@/contexts/data/MasterGroupsContext";
import TrialContext from "@/contexts/data/TrialContext";
import { faExclamationCircle } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, AlertTitle, Box, ListItemIcon, ListItemText, darken, lighten, styled } from "@mui/material";
import { FC, useCallback, useContext, useMemo } from "react";
import StatusList, { IStatusListCreateAdditional, IStatusListCreateConfigureLink, IStatusListCreateLabel, IStatusListCreateLink, IStatusListIsDisabled, IStatusListSearch, IStatusListStatsComponentProps, StatusListItemIcon, StatusListItemText, StatusListProps } from "./StatusList";
import { DefaultSortableFields, IStatusListSortable } from "./StatusListSort";
import DefaultStatComponents, { StatusListItemTooltip, StatusListItemTooltipStats, StatusListStatsBox } from './StatusListStats';
import Text, { ITextProps } from "@/components/forms/inputs/Text";
import Multiselect, { IMultiselectProps } from "@/components/forms/inputs/Multiselect";
import ChosenThemeSizeContext from "@/contexts/styles/ChosenThemeSizeContext";


export interface IInstitutionConfigurationListAdditional {
    trial?: Trial;
    masterGroup?: MasterGroup;
    coordinatingGroup?: CoordinatingGroup;
}

export interface IInstitutionConfigurationListProps extends StatusListProps<Institution, IInstitutionConfigurationListAdditional> {

}

const CodeBox = styled(Box)(({ theme }) => ({ 
    display: 'flex',
    alignItems: 'center',
    width: theme.spacing(10),
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    background: theme.palette.mode === 'dark' ? darken(theme.palette.primary.main,0.5) : lighten(theme.palette.primary.main, 0.5) 
}));


const createInstitutionStatusLabel: IStatusListCreateLabel<Institution, IInstitutionConfigurationListAdditional> = (itemData) => {
    return (
        <>
            <StatusListItemIcon>
                <FontAwesomeIcon fixedWidth size="lg" icon={AppSettings.icons.institution} />
            </StatusListItemIcon>
            <StatusListItemText 
                primary={itemData.data.name} 
            />
        </>
    )
};

const createInstitutionLink: IStatusListCreateLink<Institution, IInstitutionConfigurationListAdditional> = (itemData) => {
    
    if (itemData.additional.trial) {
        return `/${itemData.additional.trial.oid}/configuration/institutions/${itemData.data.code}`
    }

    return `/configuration/institutions/${itemData.data.code}`;
};

const createInstitutionConfigurationLink: IStatusListCreateConfigureLink<Institution, IInstitutionConfigurationListAdditional> = (itemData) => {
    return undefined;
};

const isInstitutionDisabled: IStatusListIsDisabled<Institution, IInstitutionConfigurationListAdditional> = (itemData) => {
    return !itemData.data.status || itemData.data.status === ConfigurationStatus.Disabled;
};

const isInstitutionListItemDisabled: IStatusListIsDisabled<Institution, IInstitutionConfigurationListAdditional> = (itemData) => {
    return false;
};


const InstitutionStatsComponent = ({
    itemData, 
    theme
}: IStatusListStatsComponentProps<Institution, IInstitutionConfigurationListAdditional>) => {
    return (
        <StatusListStatsBox>
            <DefaultStatComponents.DisabledIndicatorStats itemData={itemData} theme={theme} />
            <DefaultStatComponents.ReadOnlyIndicatorStats itemData={itemData} theme={theme} />
            <StatusListItemTooltip
                placement="left"
                title={
                    <>
                        <strong>Institution</strong>
                        <StatusListItemTooltipStats>
                            <span>
                                Code
                            </span>
                            <span>
                                {itemData.data.code}
                            </span>
                        </StatusListItemTooltipStats>
                        {
                            itemData.additional.masterGroup && (
                                <StatusListItemTooltipStats>
                                    <span>
                                        Master Group
                                    </span>
                                    <span>
                                        {itemData.additional.masterGroup.name}
                                    </span>
                                </StatusListItemTooltipStats>
                            )
                        }
                        {
                            itemData.additional.coordinatingGroup && (
                                <StatusListItemTooltipStats>
                                    <span>
                                        Coordinating Group
                                    </span>
                                    <span>
                                        {itemData.additional.coordinatingGroup.name}
                                    </span>
                                </StatusListItemTooltipStats>
                            )
                        }
                    </>
                }
            >
                <CodeBox>
                    {itemData.data.code}
                </CodeBox>
            </StatusListItemTooltip>
        </StatusListStatsBox>
    )
};


const SortableFields: IStatusListSortable<Institution, IInstitutionConfigurationListAdditional>[] = [
    {
        name: 'Name',
        sort: (a, b) => a.data.name! > b.data.name! ? 1 : -1
    },
    {
        name: 'Code',
        sort: (a, b) => a.data.code! > b.data.code! ? 1 : -1
    },
    DefaultSortableFields.status
]

const DefaultSearchData = { statuses: [ConfigurationStatus.Enabled, ConfigurationStatus.ReadOnly] };

const InstitutionStatusList: FC<IInstitutionConfigurationListProps> = ({
    ...statusListProps
}) => {
    const { data: trial } = useContext(TrialContext);
    const { data: masterGroups, idMap: masterGroupMapping } = useContext(MasterGroupsContext);
    const { data: coordinatingGroups, idMap: coordinatingGroupMapping } = useContext(CoordinatingGroupsContext);

    const createInstitutionAdditional: IStatusListCreateAdditional<Institution, IInstitutionConfigurationListAdditional> = useCallback((itemData) => {
        return {
            trial: trial,
            masterGroup: masterGroupMapping && itemData.data.masterGroupId ? masterGroupMapping[itemData.data.masterGroupId] : undefined,
            coordinatingGroup: coordinatingGroupMapping && itemData.data.coordinatingGroupId ? coordinatingGroupMapping[itemData.data.coordinatingGroupId] : undefined
        }
    }, [trial, masterGroupMapping, coordinatingGroupMapping])


    

    const searchableFields: IStatusListSearch<Institution, IInstitutionConfigurationListAdditional>[] = useMemo(() => {
        const output: IStatusListSearch<Institution, IInstitutionConfigurationListAdditional, any, any>[] = [
            {
                key: 'studyNumber',
                component: Text,
                componentProps: {
                    margin: 'normal'
                } as ITextProps,
                label: 'Study Number',
                searchFn: (item, value) => value ? item.data.name?.toLowerCase()?.includes(value?.toLowerCase()) ?? false : false
            } as IStatusListSearch<Institution, IInstitutionConfigurationListAdditional, string>
        ]
        
        if (masterGroups && masterGroups.length > 0) {
            output.push({
                key: 'masterGroups',
                component: Multiselect,
                componentProps: {
                    options: masterGroups?.map(x => ({
                        id: x.id,
                        name: x.name
                    })) ?? [],
                    FormControlProps: {
                        margin: 'normal'
                    }
                } as IMultiselectProps,
                label: 'Master Groups',
                searchFn: (item, value) => value && value.length > 0 ?
                    item.data.masterGroupId ?
                        value.includes(item.data.masterGroupId) ?? false : 
                        false :
                    true
            } as IStatusListSearch<Institution, IInstitutionConfigurationListAdditional, number[]>);
        }

        if (coordinatingGroups && coordinatingGroups.length > 0) {
            output.push({
                key: 'coordinatingGroups',
                component: Multiselect,
                componentProps: {
                    options: coordinatingGroups?.map(x => ({
                        id: x.id,
                        name: x.name
                    })) ?? [],
                    optionsFilter: (options, formState) => {
                        if (!formState.value.masterGroups || formState.value.masterGroups.length === 0) {
                            return options;
                        }

                        return options.filter(x => coordinatingGroupMapping && formState.value.masterGroups.includes(coordinatingGroupMapping[x.id].masterGroupId))
                    },
                    FormControlProps: {
                        margin: 'normal'
                    }
                    
                } as IMultiselectProps,
                label: 'Coordinating Groups',
                searchFn: (item, value) => value && value.length > 0 ?
                    item.data.coordinatingGroupId ?
                        value.includes(item.data.coordinatingGroupId) ?? false : 
                        false :
                    true
            } as IStatusListSearch<Institution, IInstitutionConfigurationListAdditional, number[]>);
        }

        output.push({
            key: 'statuses',
            component: Multiselect,
            componentProps: {
                options: [
                    {
                        id: ConfigurationStatus.Disabled,
                        name: 'Disabled',
                        description: `The participant is disabled in the Source Data Verification system.`
                    },
                    {
                        id: ConfigurationStatus.ReadOnly,
                        name: 'Read Only',
                        description: `The participant is read only in the Source Data Verification system.`
                    },
                    {
                        id: ConfigurationStatus.Enabled,
                        name: 'Enabled',
                        description: `The participant is enabled in the Source Data Verification system.`
                    }
                ],
                FormControlProps: {
                    margin: 'normal'
                }
                
            } as IMultiselectProps,
            label: 'Statuses',
            searchFn: (item, value) => value && value.length > 0 ?
                item.data.status ?
                    value.includes(item.data.status) ?? false : 
                    false :
                true
        } as IStatusListSearch<Institution, IInstitutionConfigurationListAdditional, ConfigurationStatus[]>)

        return output;
    }, [masterGroups, masterGroupMapping, coordinatingGroups, coordinatingGroupMapping]);
    

    const themeSize = useContext(ChosenThemeSizeContext);

    const height = useMemo(() => {
        switch (themeSize) {
            case 'extra-small':
                return 25;
            case 'small':
                return 37;
            case 'medium':
                return 49;
            case 'large':
                return 61;
            case 'extra-large':
                return 73;
        }
    }, [themeSize])

    return (
        <StatusList 
            {...statusListProps}
            createLabel={createInstitutionStatusLabel}
            createLink={createInstitutionLink}
            createConfigureLink={createInstitutionConfigurationLink}
            isDisabled={isInstitutionDisabled}
            isListItemDisabled={isInstitutionListItemDisabled}
            createAdditional={createInstitutionAdditional}
            StatsComponent={InstitutionStatsComponent}
            sortKey="configuration-institutions-sort"
            sortableFields={SortableFields}
            noRecords={
                <Alert
                    severity="warning"
                >
                    <AlertTitle>No Institutions</AlertTitle>
                    No institutions were found on the {trial?.name} trial.
                </Alert>
            }
            searchKey={`configuration-institutions-search-${trial?.oid}`}
            searchFields={searchableFields}
            fixedItemHeight={height}
            defaultSearchData={DefaultSearchData}
        />
    )
};

export default InstitutionStatusList;